/**
 * Locale-aware formatting helpers for the conversation surface (the operator inbox and the
 * Customer 360 conversation tab — design-conversations-inbox.md §6/§7/§11).
 *
 * All formatting goes through `Intl.*` (never a hardcoded unit word or date order) per the design
 * system's locale-awareness rule — see `obzr-design-system`. Callers pass `getIntlLocale()` fresh on
 * every render (never cache it) so formatting stays reactive to a language switch.
 */

/** "14:01" — a bare clock reading, used in message meta lines. */
export function formatClock(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(value),
  );
}

/** "9 Sep, 14:01" (locale order/punctuation vary) — day + month + time, no year (recent events only). */
export function formatDayTime(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

/** "9 September 2026" — the timeline's day-separator format (design §7.4: `dateStyle: 'long'`). */
export function formatDayLong(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(value));
}

/** True when both instants fall on the same calendar day in the given locale/timezone. */
export function isSameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

const RELATIVE_DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/** "5 minutes ago" / "in 2 hours" — a fully locale-aware relative-time label. */
export function relativeTimeLabel(value: string, locale: string, now: number = Date.now()): string {
  let duration = (new Date(value).getTime() - now) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return rtf.format(Math.round(duration), 'year');
}

/** "12 min" / "2 h 10 min" — a compact locale-aware duration, for waiting/expiry captions. */
export function formatDurationShort(ms: number, locale: string): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const unit = (value: number, u: 'hour' | 'minute') =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: u, unitDisplay: 'short' }).format(value);
  return hours > 0 ? `${unit(hours, 'hour')} ${unit(minutes, 'minute')}` : unit(minutes, 'minute');
}

/** "1.2 MB" — file size, for attachment captions. */
export function formatBytes(bytes: number, locale: string): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
  return `${formatted} ${units[unitIndex]}`;
}

/** "0:42" — a media duration in minutes:seconds, for voice/video captions. */
export function formatMediaDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
